import React, { useEffect, useCallback, useState } from 'react'
import { Main, MainBody, MainTop, MainTopTitle, WhiteBox, BoxHeader } from '@/components/Layout'
import { Col, Row } from 'reactstrap'
import Resx from '@/components/Resx'
import IndividualDetail from '@/internal-app/pages/dataCrossing/individual-detail'
import { useReduxState } from '@/hooks/reduxState'
import { useDispatch } from '@/hooks/dispatch'
import { actionCreators } from '@/store/dataCrossing'
import LoadingIndicator from '@/components/LoadingIndicator'
import { dialogTypes } from '@/dialogs'
import context from '@/context'
import { useLocalization } from '@/hooks/localization'
import { actionCreators as dialogActionCreators } from '@/store/Dialogs'
import { push } from 'react-router-redux'
import { TabContent, TabNavItems, TabPane } from '@/components/tabs'
import { duplicatesTabs } from '@/internal-app/pages/dataCrossing/duplicates'
import { IndividualsComparisonDetail } from '@/internal-app/pages/dataCrossing/individual-comparison-detail'
import { usePermissionChecker } from '@/hooks/usePermissionChecker'
import { getName } from '@/utils/nameHelpers'

export const duplicateDetailTabs = {
  duplicates: 'duplicates',
  scoring: 'scoring'
}

export const DataCrossingDuplicateDetail = React.memo(function DataCrossingDuplicateDetail (props) {
  const [changingDuplicity, setChangingDuplicity] = useState(false)
  const [tabId, setTabId] = useState(duplicateDetailTabs.duplicates)
  const realPersonId = props.match.params.id
  const { localize } = useLocalization()
  const isGranted = usePermissionChecker()

  const [delinquentIndividuals, delinquentRetrieving] = useReduxState(useCallback(state => ([
    state.dataCrossing.delinquentIndividuals,
    state.dataCrossing.delinquentRetrieving
  ]), []))

  const actions = useDispatch({
    openDialog: dialogActionCreators.openDialog,
    getDuplicateDetail: actionCreators.getDuplicateDetail,
    push: push
  })

  useEffect(() => {
    if (!changingDuplicity) {
      actions.getDuplicateDetail(realPersonId)
    }
  }, [actions, changingDuplicity, realPersonId])

  const onAddNewDuplicityClick = useCallback(() => {
    actions.openDialog({
      dialogType: dialogTypes.addIndividualDuplicity,
      title: 'DataCrossings.AddNewDuplicity',
      primaryButtonText: 'DataCrossings.MarkAsDuplicity',
      onlySecondIndividualSelection: true,
      onAction: ({ secondIndividualId }) => {
        setChangingDuplicity(true)
        context.api.dataCrossingService.setIndividualComparison({
          id: delinquentIndividuals.individuals[0].id,
          secondId: secondIndividualId,
          isSameIndividual: true,
          isMarkedManually: true })
          .then(() => {
            setChangingDuplicity(false)
          })
          .catch(() => setChangingDuplicity(false))
      }
    })
  }, [actions, delinquentIndividuals.individuals])

  const onRemoveDuplicityClick = useCallback((id) => {
    actions.openDialog({
      dialogType: dialogTypes.confirm,
      title: 'DataCrossings.RemoveDuplicityPromptTitle',
      content: localize('DataCrossings.RemoveDuplicityPrompt'),
      onAction: () => {
        setChangingDuplicity(true)
        context.api.dataCrossingService.removeIndividualDuplicate(realPersonId, id)
          .then(() => {
            if (delinquentIndividuals.individuals.length === 2) {
              actions.push(`/data-crossings-duplicates/${duplicatesTabs.duplicateList}`)
              return
            }
            setChangingDuplicity(false)
          })
          .catch(() => setChangingDuplicity(false))
      }
    })
  }, [actions, localize, realPersonId, delinquentIndividuals.individuals])

  return (
    <Main>
      <MainTop>
        <MainTopTitle backUrl={`/data-crossings-duplicates/${duplicatesTabs.duplicateList}`}>
          <Resx name='DataCrossings.DuplicityDetail' />
        </MainTopTitle>
      </MainTop>
      <MainBody transparent>
        {isGranted('CanManageDataCrossingsDuplicates') && <p className='btn-group'>
          <button className='btn btn-md btn-primary' onClick={onAddNewDuplicityClick}>
            <Resx name='DataCrossings.AddNewDuplicity' /></button>
        </p>}
        <WhiteBox>
          <TabNavItems activeTabId={tabId} onClick={setTabId} items={[
            { id: duplicateDetailTabs.duplicates, text: 'DataCrossings.Duplicates' },
            { id: duplicateDetailTabs.scoring, text: 'DataCrossings.Scoring' }
          ]} />
          <TabContent activeTab={tabId}>
            <TabPane tabId={duplicateDetailTabs.duplicates}>
              <Row className='detail-boxes'>
                {delinquentRetrieving || changingDuplicity
                  ? <LoadingIndicator />
                  : delinquentIndividuals.individuals.map(x =>
                    <WhiteBox key={`whiteBox_${x.id}`}>
                      <Col>
                        <div className='comparison-header'>
                          <BoxHeader className='header'>{getName(x)}</BoxHeader>
                        </div>
                        <IndividualDetail individual={x} localize={localize} />
                        <br />
                        {isGranted('CanManageDataCrossingsDuplicates') && <p className='btn-group'>
                          <button className='btn btn-md btn-danger' onClick={() => onRemoveDuplicityClick(x.id)}>
                            <Resx name='DataCrossings.RemoveIndividualDuplicity' />
                          </button>
                        </p>}
                      </Col>
                    </WhiteBox>
                  )
                }
              </Row>
            </TabPane>
            <TabPane tabId={duplicateDetailTabs.scoring}>
              {delinquentRetrieving || changingDuplicity
                ? <LoadingIndicator />
                : <React.Fragment>
                  <h4><Resx name='DataCrossings.ScoringDetail' /></h4>
                  {delinquentIndividuals.comparisons.map(x => (
                    <React.Fragment key={`duplicateDetailScoring_${x.id}`}>
                      <Row>
                        <IndividualsComparisonDetail key={`${x.id}`} {...x} />
                      </Row>
                      <p className='text-center font-weight-bold'>
                        <Resx name='DataCrossings.AccordanceWeight' />: {x.totalWeight}%
                      </p>
                      <hr />
                    </React.Fragment>
                  ))}
                </React.Fragment>
              }
            </TabPane>
          </TabContent>
        </WhiteBox>
      </MainBody>
    </Main>
  )
})



// WEBPACK FOOTER //
// ./src/internal-app/pages/dataCrossing/duplicate-detail.js